import React, { useState, useEffect } from "react";
import { EmblaOptionsType } from "embla-carousel";
import { getDaysInMonth } from "date-fns";
import { IosPickerItem } from "./EmblaCarouselIosPickerItem";
import { getMonthNames } from "@/shared/utils";
import { useTranslation } from "react-i18next";

type PropType = {
  loop?: EmblaOptionsType["loop"];
  value: Date | string | undefined;
  onValueChange?: (value: Date) => void;
};

const MAX_YEAR = new Date().getFullYear() - 16;
const MIN_YEAR = 1950;
const YEAR_RANGE = MAX_YEAR - MIN_YEAR + 1;

const areDatesEqual = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const IOSDatePicker: React.FC<PropType> = ({
  loop,
  value = new Date(),
  onValueChange,
}) => {
  const { i18n } = useTranslation();
  const initialDate = new Date(value);
  const [monthNames, setMonthNames] = useState<string[]>(() => getMonthNames());
  const [selectedDay, setSelectedDay] = useState(initialDate.getDate());
  const [selectedMonth, setSelectedMonth] = useState(initialDate.getMonth());
  const [selectedYear, setSelectedYear] = useState(() => {
    const date = new Date(value);
    return Math.min(date.getFullYear(), MAX_YEAR);
  });
  const currentLanguage = i18n.language;

  useEffect(() => {
    setMonthNames(getMonthNames());
  }, [currentLanguage]);

  useEffect(() => {
    const daysInNewMonth = getDaysInMonth(
      new Date(selectedYear, selectedMonth)
    );
    const clampedDay = Math.min(selectedDay, daysInNewMonth);

    if (clampedDay !== selectedDay) {
      setSelectedDay(clampedDay);
    }
  }, [selectedMonth, selectedYear, selectedDay]);

  useEffect(() => {
    const daysInMonth = getDaysInMonth(new Date(selectedYear, selectedMonth));
    const safeDay = Math.min(selectedDay, daysInMonth);
    const newDate = new Date(selectedYear, selectedMonth, safeDay);
    const originalDate = new Date(value);

    if (!areDatesEqual(newDate, originalDate)) {
      onValueChange?.(newDate);
    }
  }, [selectedDay, selectedMonth, selectedYear, onValueChange, value]);

  return (
    <div className="embla">
      <IosPickerItem
        items={Array.from(
          { length: getDaysInMonth(new Date(selectedYear, selectedMonth)) },
          (_, i) => ({ label: `${i + 1}`, value: i + 1 })
        )}
        perspective="left"
        key={`days-${selectedMonth}-${selectedYear}`}
        loop={loop}
        value={selectedDay}
        onValueChange={setSelectedDay}
      />
      <IosPickerItem
        items={monthNames.map((name, index) => ({
          label: name,
          value: index,
        }))}
        perspective="center"
        className="flex-1"
        loop={loop}
        value={selectedMonth}
        onValueChange={setSelectedMonth}
      />
      <IosPickerItem
        items={Array.from({ length: YEAR_RANGE }, (_, i) => {
          const year = MIN_YEAR + i;
          return { label: `${year}`, value: year };
        })}
        perspective="right"
        loop={loop}
        value={selectedYear}
        onValueChange={setSelectedYear}
      />
    </div>
  );
};

export default IOSDatePicker;
